
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useCountry } from '@/contexts/country'; 
import { useConstituencies } from './constituencies/useConstituencies';
import ConstituencyFilters from './constituencies/ConstituencyFilters';
import ConstituencyTable from './constituencies/ConstituencyTable';
import ConstituenciesPagination from './constituencies/ConstituenciesPagination';
import ConstituencyFormDialog from './constituencies/ConstituencyFormDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { useConstituenciesPagination } from '@/hooks/admin/constituencies/useConstituenciesPagination';

const CountryConstituencies = () => {
  const { availableCountries } = useCountry();
  const {
    constituencies,
    loading,
    dialogOpen,
    setDialogOpen,
    editingConstituency,
    handleAddNew,
    handleEdit,
    handleSubmit,
    handleDelete,
    toggleActive
  } = useConstituencies();
  
  const isMobile = useIsMobile();
  
  const {
    paginatedConstituencies,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    searchTerm,
    setSearchTerm,
    countryFilter,
    setCountryFilter,
    statusFilter,
    setStatusFilter,
    resetFilters,
    getCountryName
  } = useConstituenciesPagination({
    constituencies,
    availableCountries
  });

  // Count active filters
  const activeFiltersCount = [
    searchTerm ? 1 : 0,
    countryFilter !== 'all' ? 1 : 0,
    statusFilter !== 'all' ? 1 : 0
  ].reduce((sum, count) => sum + count, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Constituencies Management</CardTitle>
        <CardDescription>
          Manage constituencies for each country in the system
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className={`${isMobile ? 'flex flex-col space-y-4' : 'flex items-center justify-between'} mb-4 gap-4`}>
          <ConstituencyFilters
            selectedCountry={countryFilter}
            setSelectedCountry={setCountryFilter}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            availableCountries={availableCountries}
            activeFiltersCount={activeFiltersCount}
            onClearFilters={resetFilters}
          />
          
          <Button onClick={handleAddNew} className={isMobile ? "w-full mt-2" : "ml-auto"}>
            <Plus size={16} className="mr-2" />
            Add Constituency
          </Button>
        </div>
        
        <ConstituencyTable
          constituencies={paginatedConstituencies}
          loading={loading}
          availableCountries={availableCountries}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={toggleActive}
          getCountryName={getCountryName}
        />

        <ConstituenciesPagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </CardContent>
      
      <ConstituencyFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingConstituency={editingConstituency}
        availableCountries={availableCountries}
        onSubmit={handleSubmit}
      />
    </Card>
  );
};

export default CountryConstituencies;
